/**
 * Haptic Feedback Manager
 *
 * Provides haptic/vibration feedback for mobile devices using the Vibration API.
 * Includes smart patterns for different game events and UI interactions.
 *
 * Features:
 * - Growing charge vibration pattern
 * - Impact feedback (landing, death)
 * - UI interaction feedback
 * - Settings toggle to enable/disable
 * - Browser compatibility checks
 */

export class HapticManager {
  private static enabled: boolean = true;
  private static isSupported: boolean = false;
  private static chargeInterval: number | null = null;
  private static lastChargeIntensity: number = 0;

  /**
   * Initialize haptic manager and check browser support
   */
  static initialize(): void {
    // Check if Vibration API is supported
    this.isSupported = 'vibrate' in navigator;

    // Load haptics setting from localStorage
    const saved = localStorage.getItem('blobo_haptics_enabled');
    this.enabled = saved !== null ? saved === 'true' : true; // Default: enabled

    if (this.isSupported) {
      console.log('✅ Haptic feedback supported and enabled');
    } else {
      console.log('⚠️ Haptic feedback not supported on this device');
    }
  }

  /**
   * Enable haptic feedback
   */
  static enable(): void {
    this.enabled = true;
    localStorage.setItem('blobo_haptics_enabled', 'true');
  }

  /**
   * Disable haptic feedback
   */
  static disable(): void {
    this.enabled = false;
    this.stopCharging(); // Stop any ongoing vibration
    localStorage.setItem('blobo_haptics_enabled', 'false');
  }

  /**
   * Toggle haptic feedback on/off
   */
  static toggle(): boolean {
    if (this.enabled) {
      this.disable();
    } else {
      this.enable();
      // Give immediate feedback to confirm it's enabled
      this.buttonTap();
    }
    return this.enabled;
  }

  /**
   * Check if haptics are enabled
   */
  static isEnabled(): boolean {
    return this.enabled && this.isSupported;
  }

  /**
   * Trigger vibration (internal method)
   */
  private static vibrate(pattern: number | number[]): void {
    if (!this.enabled || !this.isSupported) return;

    try {
      navigator.vibrate(pattern);
    } catch (error) {
      console.warn('Vibration API error:', error);
    }
  }

  // ==================== GAME EVENT PATTERNS ====================

  /**
   * Start charging vibration - grows with charge intensity
   * @param intensity - Charge intensity (0 to 1)
   */
  static startCharging(intensity: number): void {
    if (!this.enabled || !this.isSupported) return;

    // Only update if intensity changed by at least 2% to avoid excessive updates
    const intensityChange = Math.abs(intensity - this.lastChargeIntensity);
    if (intensityChange < 0.02) return;

    this.lastChargeIntensity = intensity;

    // Stop previous pattern
    this.stopCharging();

    // Create pulsing pattern that gets more frequent and stronger with charge
    // Low charge: slow, subtle pulses (200ms interval, 15ms duration)
    // Full charge: fast, strong pulses (30ms interval, 50ms duration)
    const pulseInterval = 200 - intensity * 170; // 200ms -> 30ms (faster buildup)
    const pulseDuration = Math.floor(15 + intensity * 35); // 15ms -> 50ms (more noticeable)

    // Start repeating pulse
    this.chargeInterval = window.setInterval(() => {
      this.vibrate(pulseDuration);
    }, pulseInterval);
  }

  /**
   * Stop charging vibration
   */
  static stopCharging(): void {
    if (this.chargeInterval !== null) {
      clearInterval(this.chargeInterval);
      this.chargeInterval = null;
    }
    this.lastChargeIntensity = 0;
    this.vibrate(0); // Cancel any ongoing vibration
  }

  /**
   * Jump release - sharp burst when releasing jump, scaled by charge
   * @param chargePercent - Charge percentage (0 to 1)
   */
  static jumpRelease(chargePercent: number = 1): void {
    this.stopCharging(); // Stop charge vibration

    // Scale vibration by charge percentage
    // Small hop (20%): 20ms
    // Full jump (100%): 60ms
    const duration = Math.floor(20 + chargePercent * 40);
    this.vibrate(duration);
  }

  /**
   * Landing impact - intensity based on fall speed
   * @param velocity - Fall velocity (used to scale intensity)
   */
  static landing(velocity: number = 1): void {
    if (!this.enabled || !this.isSupported) return;

    // Scale impact duration by velocity
    // Light landing: 5ms, Hard landing: 20ms (75% reduced from original)
    const intensity = Math.min(velocity / 20, 1); // Normalize velocity
    const duration = Math.floor(5 + intensity * 15);

    this.vibrate(duration);
  }

  /**
   * Bounce - light tap for each bounce
   */
  static bounce(): void {
    this.vibrate(15); // Quick light tap
  }

  /**
   * Death/fall - strong impact pattern
   */
  static death(): void {
    // Strong impact pattern: [vibrate, pause, vibrate]
    this.vibrate([100, 50, 100]);
  }

  /**
   * Victory - celebration burst pattern
   */
  static victory(): void {
    // Celebration pattern: rapid bursts
    this.vibrate([50, 30, 50, 30, 80, 40, 100]);
  }

  /**
   * Zone transition - subtle notification
   */
  static zoneTransition(): void {
    this.vibrate([30, 20, 30]);
  }

  /**
   * Platform collision - light feedback
   */
  static platformCollision(): void {
    this.vibrate(10); // Very light tap
  }

  // ==================== UI INTERACTION PATTERNS ====================

  /**
   * Button tap - standard UI feedback
   */
  static buttonTap(): void {
    this.vibrate(20); // Light tap
  }

  /**
   * Settings toggle - confirmation feedback
   */
  static settingsToggle(): void {
    this.vibrate(30); // Medium tap
  }

  /**
   * Level select - selection feedback
   */
  static levelSelect(): void {
    this.vibrate(25); // Light-medium tap
  }

  /**
   * Slider drag - subtle feedback during adjustments
   */
  static sliderDrag(): void {
    this.vibrate(5); // Very subtle tap
  }

  /**
   * Error/invalid action - double tap pattern
   */
  static error(): void {
    this.vibrate([50, 30, 50]);
  }

  /**
   * Success - single confirmation
   */
  static success(): void {
    this.vibrate(40);
  }
}

// Initialize on module load
HapticManager.initialize();
